import * as Axios from '@/service/AxiosService'
import config from '@/const/const.js'
const constant = config.constant

/** タイムライン画面 State初期値 */
const defaultTimelineState = Object.freeze({
  commentList: [], // タイムラインに表示する投稿一覧
  isFetchFailed: false, // タイムライン取得失敗フラグ
  offset: 0, // ページネーション用
  scrollPosition: 0, // スクロール位置
})
/** 投稿詳細画面 State初期値 */
const defaultCommentDetailState = Object.freeze({
  selectedComment: {}, // 表示記事
  scrollPosition: 0, // スクロール位置
})
/** 他ユーザーマイページ画面 State初期値 */
const defaultOtherUserMyPageState = Object.freeze({
  contributor: {},
  commentList: [],
  isFetchFailed: false, // タイムライン取得失敗フラグ
  offset: 0, // 取得位置
  selectedContributorId: '', // 選択中投稿者ID
  scrollPosition: 0, // 記事リストのスクロール位置
})
/** タグ検索結果画面 State初期値 */
const defaultTagSearchResultState = Object.freeze({
  commentList: [], // タグ検索結果
  isFetchFailed: false, // 取得失敗フラグ
  coord: {lat: null, lon: null}, // 保持用座標
  offset: 0, // ページネーション用
  selectTag: '', // 検索しているタグ名
  scrollPosition: 0, // 記事リストのスクロール位置
})
/** スポット詳細画面 State初期値 */
const defaultSpotDetailState = Object.freeze({
  selectedSpot: {},
  commentList: [], // 検索結果
  isFetchFailed: false, // 取得失敗フラグ
  offset: 0, // ページネーション用
  scrollPosition: 0, // 記事リストのスクロール位置
})
/** 周辺検索画面 State初期値 */
const defaultNearbySearchState = Object.freeze({
  selectedSpot: {}, // 選択済スポット情報
  searchRange: {}, // 検索範囲
  commentList: [], // 検索結果
  isFetchFailed: false, // 取得失敗フラグ
  selectTag: 'タグを検索', // 検索しているタグ名
  offset: 0, // ページネーション用
})

export default {
  namespaced: true,
  state: {
    timeline: structuredClone(defaultTimelineState),
    commentDetail: structuredClone(defaultCommentDetailState),
    otherUserMyPage: structuredClone(defaultOtherUserMyPageState),
    tagSearchResult: structuredClone(defaultTagSearchResultState),
    spotDetail: structuredClone(defaultSpotDetailState),
    nearbySearch: structuredClone(defaultNearbySearchState),
  },
  getters: {},
  mutations: {
    /**
     * タイムライン画面関連 更新処理
     * @param {*} state Stateオブジェクト
     * @param {Object} obj 更新情報
     * @param {String} obj.key 更新するプロパティ名(all指定時は一括更新)
     * @param {*} obj.value 更新する値
     */
    setTimelineInfo(state, obj) {
      // allが指定されていれば、すべての項目を更新
      if (obj.key == 'all') {
        state.timeline = obj.value
        return
      }
      // 指定されていない場合はプロパティ単位で更新(許容しないプロパティを指定した場合は何もしない)
      const keyList = Object.keys(state.timeline)
      if (!keyList.includes(obj.key)) return

      state.timeline[obj.key] = obj.value
    },
    /**
     * 投稿詳細画面関連 更新処理
     * @param {*} state Stateオブジェクト
     * @param {Object} obj 更新情報
     * @param {String} obj.key 更新するプロパティ名(all指定時は一括更新)
     * @param {*} obj.value 更新する値
     */
    setCommentDetailInfo(state, obj) {
      // allが指定されていれば、すべての項目を更新
      if (obj.key == 'all') {
        state.commentDetail = obj.value
        return
      }
      // 指定されていない場合はプロパティ単位で更新(許容しないプロパティを指定した場合は何もしない)
      const keyList = Object.keys(state.commentDetail)
      if (!keyList.includes(obj.key)) return

      state.commentDetail[obj.key] = obj.value
    },
    /**
     * 他ユーザーマイページ画面関連 更新処理
     * @param {*} state Stateオブジェクト
     * @param {Object} obj 更新情報
     * @param {String} obj.key 更新するプロパティ名(all指定時は一括更新)
     * @param {*} obj.value 更新する値
     */
    setOtherUserMyPageInfo(state, obj) {
      // allが指定されていれば、すべての項目を更新
      if (obj.key == 'all') {
        state.otherUserMyPage = obj.value
        return
      }
      // 指定されていない場合はプロパティ単位で更新(許容しないプロパティを指定した場合は何もしない)
      const keyList = Object.keys(state.otherUserMyPage)
      if (!keyList.includes(obj.key)) return

      state.otherUserMyPage[obj.key] = obj.value
    },
    /**
     * タグ検索結果画面 更新処理
     * @param {*} state Stateオブジェクト
     * @param {Object} obj 更新情報
     * @param {String} obj.key 更新するプロパティ名(all指定時は一括更新)
     * @param {*} obj.value 更新する値
     */
    setTagSearchResultInfo(state, obj) {
      // allが指定されていれば、すべての項目を更新
      if (obj.key == 'all') {
        state.tagSearchResult = obj.value
        return
      }
      // 指定されていない場合はプロパティ単位で更新(許容しないプロパティを指定した場合は何もしない)
      const keyList = Object.keys(state.tagSearchResult)
      if (!keyList.includes(obj.key)) return

      state.tagSearchResult[obj.key] = obj.value
    },
    /**
     * 地点詳細画面 更新処理
     * @param {*} state Stateオブジェクト
     * @param {Object} obj 更新情報
     * @param {String} obj.key 更新するプロパティ名(all指定時は一括更新)
     * @param {*} obj.value 更新する値
     */
    setSpotDetailInfo(state, obj) {
      // allが指定されていれば、すべての項目を更新
      if (obj.key == 'all') {
        state.spotDetail = obj.value
        return
      }
      // 指定されていない場合はプロパティ単位で更新(許容しないプロパティを指定した場合は何もしない)
      const keyList = Object.keys(state.spotDetail)
      if (!keyList.includes(obj.key)) return

      state.spotDetail[obj.key] = obj.value
    },
    /**
     * 周辺検索画面 更新処理
     * @param {*} state Stateオブジェクト
     * @param {Object} obj 更新情報
     * @param {String} obj.key 更新するプロパティ名(all指定時は一括更新)
     * @param {*} obj.value 更新する値
     */
    setNearbySearchInfo(state, obj) {
      // allが指定されていれば、すべての項目を更新
      if (obj.key == 'all') {
        state.nearbySearch = obj.value
        return
      }
      // 指定されていない場合はプロパティ単位で更新(許容しないプロパティを指定した場合は何もしない)
      const keyList = Object.keys(state.nearbySearch)
      if (!keyList.includes(obj.key)) return

      state.nearbySearch[obj.key] = obj.value
    },
    /**
     * タイムライン画面関連 初期化処理
     * @param {*} state Stateオブジェクト
     */
    initTimelineInfo(state) {
      state.timeline = structuredClone(defaultTimelineState)
    },
    /**
     * 投稿詳細画面関連 初期化処理
     * @param {*} state Stateオブジェクト
     */
    initCommentDetailInfo(state) {
      state.commentDetail = structuredClone(defaultCommentDetailState)
    },
    /**
     * 他ユーザーマイページ画面関連 初期化処理
     * @param {*} state Stateオブジェクト
     */
    initOtherUserMyPageInfo(state) {
      state.otherUserMyPage = structuredClone(defaultOtherUserMyPageState)
    },
    /**
     * タグ検索結果画面 初期化処理
     * @param {*} state Stateオブジェクト
     */
    initTagSearchResultInfo(state) {
      state.tagSearchResult = structuredClone(defaultTagSearchResultState)
    },
    /**
     * 地点詳細画面 初期化処理
     * @param {*} state Stateオブジェクト
     */
    initSpotDetailInfo(state) {
      state.spotDetail = structuredClone(defaultSpotDetailState)
    },
    /**
     * 周辺検索画面 初期化処理
     * @param {*} state Stateオブジェクト
     */
    initNearbySearchInfo(state) {
      state.nearbySearch = structuredClone(defaultNearbySearchState)
    },
  },
  actions: {
    /**
     * タイムライン用投稿データ取得処理
     * @param {Function} success 成功時コールバック
     * @param {Function} failed 失敗コールバック
     * @param {Function} error エラーコールバック
     * @param {String} lat 緯度
     * @param {String} lon 軽度
     * @param {String} range 範囲(km)
     * @param {String} sortName ソート対象名
     * @param {String} sortPattern ソートパターン
     * @param {Number} offset ページ数
     * @return 現在地座標付近のスポットに対する投稿データ
     */
    fetchTimeline(
      _,
      {success, failed, error, lat, lon, range, sortName, sortPattern, offset}
    ) {
      return Axios.get('dokoiku/comment', {
        lat: lat,
        lon: lon,
        range,
        sortName: sortName,
        sortPattern: sortPattern,
        offset: offset,
      })
        .then((res) => {
          if (res.statusCode === constant.API_STATUS_CODE_OK) {
            if (success) {
              success(res.body)
            }
          } else {
            throw res
          }
        })
        .catch((e) => {
          if (e instanceof Error) {
            if (error) {
              error(e)
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw e
          } else {
            if (failed) {
              failed()
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw new Error()
          }
        })
    },
    /**
     * タグ候補検索処理
     * @param {Function} success 成功時コールバック
     * @param {Function} failed 失敗コールバック
     * @param {Function} error エラーコールバック
     * @param {String} word 検索文字
     * @return タグ候補一覧
     */
    searchTagSuggestion(_, {success, failed, error, word}) {
      return Axios.get('dokoiku/tagSuggestion', {tag: word})
        .then((res) => {
          if (res.statusCode === constant.API_STATUS_CODE_OK) {
            if (success) {
              success(res.body)
            }
          } else {
            throw res
          }
        })
        .catch((e) => {
          if (e instanceof Error) {
            if (error) {
              error(e)
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw e
          } else {
            if (failed) {
              failed()
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw new Error()
          }
        })
    },
    /**
     * 投稿者IDでの投稿データ取得処理
     * @param {Function} success 成功時コールバック
     * @param {Function} failed 失敗コールバック
     * @param {Function} error エラーコールバック
     * @param {String} contributorId 投稿者ID
     * @param {String} sortName ソート対象名
     * @param {String} sortPattern ソートパターン
     * @param {Number} offset ページ数
     * @return 投稿者IDに紐づく投稿データ
     */
    fetchCommentListByAuthor(
      _,
      {success, failed, error, contributorId, sortName, sortPattern, offset}
    ) {
      const payload = {contributorId, sortName, sortPattern, offset}
      return Axios.get('dokoiku/comment', payload)
        .then((res) => {
          if (res.statusCode === constant.API_STATUS_CODE_OK) {
            if (success) {
              success(res.body)
            }
          } else {
            throw res
          }
        })
        .catch((e) => {
          if (e instanceof Error) {
            if (error) {
              error(e)
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw e
          } else {
            if (failed) {
              failed()
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw new Error()
          }
        })
    },
    /**
     * 投稿者データ取得処理
     * @param {Function} success 成功時コールバック
     * @param {Function} failed 失敗コールバック
     * @param {Function} error エラーコールバック
     * @param {String} contributorId 投稿者ID
     * @return 投稿者データ
     */
    getContributor(_, {success, failed, error, contributorId}) {
      return Axios.get('dokoiku/contributor', {contributorId})
        .then((res) => {
          if (res.statusCode === constant.API_STATUS_CODE_OK) {
            if (success) {
              success(res.body)
            }
          } else {
            throw res
          }
        })
        .catch((e) => {
          if (e instanceof Error) {
            if (error) {
              error(e)
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw e
          } else {
            if (failed) {
              failed()
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw new Error()
          }
        })
    },
    /**
     * 地点コードでの投稿データ取得処理
     * @param {Function} success 成功時コールバック
     * @param {Function} failed 失敗コールバック
     * @param {Function} error エラーコールバック
     * @param {String} spotCode 地点コード
     * @param {String} sortName ソート対象名
     * @param {String} sortPattern ソートパターン
     * @param {Number} offset ページ数
     * @return 投稿者IDに紐づく投稿データ
     */
    fetchCommentListBySpotCode(
      _,
      {success, failed, error, spotCode, sortName, sortPattern, offset}
    ) {
      const payload = {spotCode, sortName, sortPattern, offset}
      return Axios.get('dokoiku/comment', payload)
        .then((res) => {
          if (res.statusCode === constant.API_STATUS_CODE_OK) {
            if (success) {
              success(res.body)
            }
          } else {
            throw res
          }
        })
        .catch((e) => {
          if (e instanceof Error) {
            if (error) {
              error(e)
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw e
          } else {
            if (failed) {
              failed()
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw new Error()
          }
        })
    },
    /**
     * 地点コードでの投稿データ取得処理
     * @param {Function} success 成功時コールバック
     * @param {Function} failed 失敗コールバック
     * @param {Function} error エラーコールバック
     * @param {object} filter 絞り込み条件情報
     * @param {string} filter.tag タグ名
     * @param {object} filter.area 矩形座標内のスポット情報
     * @param {string} filter.area.topLeft 矩形左上の緯度経度
     * @param {string} filter.area.bottomRight 矩形右下の緯度経度
     * @param {object} sort ソート情報
     * @param {string} sort.kind  ソート種別
     * @param {string} sort.currentCoord typeがdistanceの場合に、基準となる座標
     * @param {string} sort.order ソートパターン
     * @param {Number} offset ページ数
     * @return 投稿者IDに紐づく投稿データ
     */
    fetchSpotList(_, {success, failed, error, filter, sort, offset}) {
      const payload = {filter, sort, offset}
      return Axios.post('dokoiku/searchSpot', payload)
        .then((res) => {
          if (res.statusCode === constant.API_STATUS_CODE_OK) {
            if (success) {
              success(res.body)
            }
          } else {
            throw res
          }
        })
        .catch((e) => {
          if (e instanceof Error) {
            if (error) {
              error(e)
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw e
          } else {
            if (failed) {
              failed()
              return
            }
            // 指定がなければ共通エラーとしてハンドリング
            throw new Error()
          }
        })
    },
  },
}
